import { ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AuthDialog from "@/v2/components/AuthDialog";

/**
 * Minimal funnel chrome for Progetto Startup V2.
 * - logo/home only
 * - login/account link
 * - no menu, no marketing links
 * Reference: docs/app-flow.md §3
 */
export default function FunnelLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 font-body antialiased">
      <header className="border-b border-white/5 backdrop-blur-md bg-slate-950/40 sticky top-0 z-10">
        <div className="container flex items-center justify-between h-16">
          <Link to="/" className="font-display font-semibold tracking-tight text-base">
            Progetto Startup
          </Link>
          {user ? (
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400">
                Ciao, {user.user_metadata?.full_name?.split(" ")[0] ?? user.user_metadata?.name?.split(" ")[0] ?? user.user_metadata?.first_name ?? user.email}
              </span>
              <Link
                to="/v2/sprint"
                className="text-sm text-slate-300 hover:text-white transition-colors"
              >
                Il mio Sprint
              </Link>
              <Link
                to="/v2/account"
                className="text-sm text-slate-300 hover:text-white transition-colors"
              >
                Account
              </Link>
              <button
                type="button"
                onClick={signOut}
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" />
                Esci
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                className="text-sm text-slate-300 hover:text-white transition-colors"
              >
                Accedi
              </button>
              <AuthDialog
                open={authOpen}
                onOpenChange={setAuthOpen}
                defaultTab="signin"
                onSuccess={() => {
                  setAuthOpen(false);
                  navigate("/v2/sprint");
                }}
              />
            </>
          )}
        </div>
      </header>



      <main className="container py-10 md:py-16">{children}</main>
      <footer className="mt-24 border-t border-white/5 py-8 text-center text-xs text-slate-600 space-x-4">
        <span>© {new Date().getFullYear()} Progetto Startup</span>
        <span className="text-slate-700">·</span>
        <Link to="/privacy-policy" className="hover:text-slate-400 transition-colors">Privacy Policy</Link>
        <span className="text-slate-700">·</span>
        <Link to="/termini-e-condizioni" className="hover:text-slate-400 transition-colors">Termini e Condizioni</Link>
      </footer>
    </div>
  );
}
